import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row, Skeleton, Space, Switch, Tooltip, Typography, message } from 'antd';
import { FileTextOutlined, FilterOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import ListView from './ListView.jsx';
import SearchFieldControl from './searchBuilder/SearchFieldControl.jsx';
import BooleanQueryField from './searchBuilder/BooleanQueryField.jsx';
import { BOOLEAN_PARSEABLE_API_KEYS, isBooleanParsable, validateBooleanQuery } from './searchBuilder/booleanQueryUtils';
import { buildBooleanRequest } from './searchBuilder/buildBooleanRequest';
import { buildTraditionalParams } from './searchBuilder/buildTraditionalParams';
import { buildFilterConditions } from './searchBuilder/buildFilterConditions';
import { safeNavTarget } from '../utils/afterSubmitNav';
import { getCustomForms } from '../services/adminApi';
import { searchModuleData } from '../services/moduleDataApi';
import { searchBoolean } from '../services/booleanSearchApi';
import { makeSearchFetcher } from '../services/candidateSearchAdapter';
import { mapListResponse } from '../services/responseMapping/mapListResponse';
import { fetchJsonWithAuth } from '../services/authApi';
import { AUTH_URL } from '../services/apiConfig';
import {
  buildSourceTabs,
  getAvailableSourceActions,
  getSearchSources,
  getSourceActionInstanceKey,
  resolveSearchSource,
  runSourceAction,
  sourceActionAppliesToRecord,
} from '../services/sourceProviderActions';
import '../styles/SearchBuilderV1.css';

const { Text } = Typography;

// Picking a source is not itself a search criterion — searching on it alone
// would return the entire collection.
const SOURCE_ONLY_FIELDS = new Set(['selectedSource', 'source', 'sourceType', 'scope']);

const EMPTY_RESULTS = {
  searched: false, rows: [], total: 0, fields: [], actions: [], actionRules: [], loading: false,
};

function extractGroups(schema) {
  if (Array.isArray(schema)) return schema;
  if (Array.isArray(schema?.groups)) return schema.groups;
  if (Array.isArray(schema?.data?.groups)) return schema.data.groups;
  return [];
}

function truthy(value) {
  return value === true || value === 1 || value === '1';
}

// A locked checkbox/radio option (admin-configured — off by default, see
// FormGroupsSection's Options/Values editor) must be selected from the very
// first render, not just visually via the registry renderer's own defensive
// merge — otherwise submitting without ever touching the control would omit
// it from the request entirely. Radio only allows one selection, so its
// locked default is a single value; checkbox's is every locked option.
function lockedInitialValue(field) {
  const options = Array.isArray(field.options) ? field.options : [];
  const locked = options.filter((option) => option.locked).map((option) => option.value);
  if (!locked.length) return undefined;
  return field.type === 'radio' ? locked[0] : locked;
}

// Which module key answers for a given source tab. Two places can say so, and
// neither is required — with no mapping the form's own moduleName is used and
// nothing changes:
//   1. the source option itself (`{ value:'dice', module:'sourceCandidate' }`),
//      so it is admin config alongside the option's label and locked flag;
//   2. a `sourceModules` prop, for a host app that wants to declare it in code
//      before the option key is persisted backend-side.
// The option wins, so config beats code once it exists.
function resolveSourceModule(source, fields, sourceModules) {
  const key = String(source ?? '').trim().toLowerCase();
  if (!key) return '';

  const optionModule = fields
    .flatMap((field) => (Array.isArray(field.options) ? field.options : []))
    .find((option) => String(option?.value ?? '').trim().toLowerCase() === key)
    ?.module;

  return String(optionModule || sourceModules?.[key] || '').trim();
}

// Dice rows do not always carry _id, and ListView needs a stable key per row or
// selection attaches to the wrong record after a re-sort or page change.
function resolveRowKey(record, index) {
  return String(
    record?._id
    ?? record?.id
    ?? record?.applicantRefrenceId
    ?? record?.applicantReferanceId
    ?? record?.diceId
    ?? record?.profileId
    ?? index,
  );
}

function hasCriteria(values) {
  return Object.entries(values ?? {}).some(([key, value]) => {
    if (SOURCE_ONLY_FIELDS.has(key)) return false;
    if (value === undefined || value === null || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  });
}

export default function SearchBuilderV1({
  moduleName,
  action,
  formType = 'search',
  title,
  migrationJobId,
  sourceModules,
  searchActionContext,
  onBack,
}) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  const [searchMode, setSearchMode] = useState('traditional');
  const [selectedSource, setSelectedSource] = useState('internal');
  const [fieldErrors, setFieldErrors] = useState({});
  const [tabs, setTabs] = useState([]);
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [lastValues, setLastValues] = useState(null);
  const [sourceActionState, setSourceActionState] = useState({});
  const [formValues, setFormValues] = useState({});
  // Unified path only: the submitted criteria, and a nonce that remounts
  // ListView so pressing Search again re-runs even when nothing changed.
  const [criteria, setCriteria] = useState(null);
  const [searchNonce, setSearchNonce] = useState(0);
  const [activeView, setActiveView] = useState('list');

  // Every state write happens in a promise callback, and a config that resolves
  // after the props changed (or after unmount) is discarded rather than
  // overwriting the newer one.
  useEffect(() => {
    if (!moduleName) return undefined;
    let cancelled = false;

    getCustomForms(moduleName, formType, action)
      .then((data) => {
        if (cancelled) return;
        setSchema(Array.isArray(data) ? data[0] ?? null : data ?? null);
        setLoadError('');
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error?.message || 'Failed to load the search form configuration.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [moduleName, formType, action, reloadToken]);

  const retry = () => { setLoading(true); setReloadToken((token) => token + 1); };

  const fields = useMemo(() => (
    extractGroups(schema)
      .filter((group) => !truthy(group.hideGroup))
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
      .flatMap((group) => group.fields ?? [])
      .filter((field) => field?.field && field.show !== 0 && !truthy(field.hidden))
      .sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0))
  ), [schema]);

  // A form whose config never opted in shows no toggle at all, so every
  // existing search form behaves exactly as it did before.
  const booleanEnabled = Boolean(schema?.booleanSearchEnabled) && Boolean(schema?.booleanSearchEndpoint);
  const isBooleanMode = booleanEnabled && searchMode === 'boolean';

  // Only fields the backend actually parses as an expression may render as
  // one — see isBooleanParsable's doc comment. A field the admin marked
  // booleanCapable but mapped elsewhere (a config mistake, not a user
  // mistake) still renders traditionally; it just can't reach the broken
  // combination, so it's surfaced to whoever builds the form, not the person
  // searching.
  const booleanFields = useMemo(
    () => fields.filter((field) => field.booleanCapable && isBooleanParsable(field.booleanApiKey)),
    [fields],
  );

  useEffect(() => {
    fields
      .filter((field) => field.booleanCapable && !isBooleanParsable(field.booleanApiKey))
      .forEach((field) => {
        console.warn(
          `[SearchBuilderV1] "${field.field}" is marked booleanCapable but its booleanApiKey `
          + `(${JSON.stringify(field.booleanApiKey)}) is not one of ${BOOLEAN_PARSEABLE_API_KEYS.join(', ')} — `
          + 'the backend cannot parse it as an expression, so it will render as a normal field. Fix in Admin → Form Groups.',
        );
      });
  }, [fields]);

  const handleModeChange = (checked) => {
    const nextMode = checked ? 'boolean' : 'traditional';
    // A boolean-capable field holds a different value type in each mode (chips
    // vs. an expression string), so carrying values across would submit junk.
    const cleared = {};
    fields.forEach((field) => { if (field.booleanCapable) cleared[field.field] = undefined; });
    form.setFieldsValue(cleared);
    setFormValues((current) => ({ ...current, ...cleared }));
    setFieldErrors({});
    setCriteria(null);
    setSearchMode(nextMode);
  };

  const runSearch = useCallback(async (values, sourceOverride, paginationOverride = {}) => {
    const pageSize = Number(paginationOverride.pageSize) > 0
      ? Number(paginationOverride.pageSize) : pagination.pageSize;
    const requested = Number(paginationOverride.current);
    const current = Number.isFinite(requested) && requested > 0 ? Math.floor(requested) : 1;

    const checkedSources = getSearchSources(values?.selectedSource);
    const activeSource = sourceOverride
      ? resolveSearchSource(sourceOverride, selectedSource)
      : (checkedSources.includes('internal')
        ? 'internal'
        : resolveSearchSource(values?.selectedSource, selectedSource));

    // A source tab can be served by its OWN module key, so that tab gets its own
    // columns and row actions — the backend keeps the requested key for every
    // config lookup and falls back to the base module for the data (see
    // configModule in moduleDataController.go). No mapping for a source means
    // the form's own moduleName, exactly as before, so every other form and
    // project is unaffected.
    const activeModule = resolveSourceModule(activeSource, fields, sourceModules) || moduleName;

    setLastValues(values);
    setPagination({ current, pageSize });
    setFieldErrors({});
    setResults((prev) => ({ ...prev, loading: true, searched: true }));

    try {
      let result;
      if (isBooleanMode) {
        const body = buildBooleanRequest(values, fields, {
          defaultSourceType: schema?.booleanDefaultSourceType || activeSource,
          // Same module key the traditional endpoint gets as ?module=, so the
          // backend can resolve fields/actions/rules from the same config
          // whichever mode the search ran in.
          module: activeModule,
        });
        result = await searchBoolean(schema.booleanSearchEndpoint, body, {
          limit: pageSize,
          offset: (current - 1) * pageSize,
        });
      } else {
        // selectedSource is the full ticked list, scope is the one being viewed.
        // That is the contract the API was built for (moduleDataController.go):
        // both enabled -> internal records + a Dice count, in ONE call, with a
        // tab per source. Sending only the active source made the server return
        // a single tab and forced the client to invent the rest of the strip.
        result = await searchModuleData(
          activeModule,
          {
            ...buildTraditionalParams(values, fields),
            selectedSource: (checkedSources.length ? checkedSources : [activeSource]).join(','),
          },
          { limit: pageSize, offset: (current - 1) * pageSize },
          { scope: activeSource, filters: buildFilterConditions(values, fields) },
        );
      }

      // With several sources ticked the server's `total` is the COMBINED figure
      // (internal + dice), which makes pagination read "1-10 of 281460" on a tab
      // holding 9 records. The per-source count is the honest one for the tab
      // being viewed; fall back to the total when it is absent.
      const sourceCount = Number(result.counts?.[activeSource]);
      const pagedTotal = Number.isFinite(sourceCount) ? sourceCount : result.total;

      setSelectedSource(activeSource);
      setResults({
        searched: true,
        rows: result.rows,
        total: pagedTotal,
        fields: result.fields ?? [],
        actions: result.actions ?? [],
        actionRules: result.actionRules ?? [],
        loading: false,
      });
      setTabs((currentTabs) => buildSourceTabs({
        // The ticked options are the single source of truth for tab ORDER, in
        // both modes. The server's own list can't be: boolean search runs its
        // sources through sort.Strings (models/boolean_search.go), so it always
        // comes back alphabetical — "dice" before "internal" — which put Dice
        // first the moment the Boolean toggle went on. result.sources is only a
        // fallback for when nothing is ticked.
        sources: checkedSources.length ? checkedSources : result.sources,
        activeSource,
        activeCount: pagedTotal,
        responseCounts: result.counts,
        currentTabs,
        backendTabs: result.tabs,
      }));
    } catch (error) {
      // Point a rejected expression at the field that carries it when the
      // server names one; otherwise fall back to a form-level message.
      const details = error?.details ? String(error.details) : '';
      const blamed = error?.code === 'INVALID_BOOLEAN_QUERY'
        ? fields.find((field) => field.booleanCapable && details.toLowerCase().includes(String(field.field).toLowerCase()))
        : null;
      if (blamed) setFieldErrors({ [blamed.field]: details });
      else message.error(details || error?.message || 'Search failed');

      setResults({ ...EMPTY_RESULTS, searched: true });
    }
  }, [fields, isBooleanMode, moduleName, pagination.pageSize, schema, selectedSource, sourceModules]);

  // Which endpoint answers this form, defined in Admin (Form Groups → Form
  // Identity → Search Endpoint). When set, ListView fetches from it for BOTH
  // modes and owns tabs, paging, sort and selection. Blank — the default for
  // every form — keeps the common /module-data-list path unchanged.
  //
  // Deliberately NOT booleanSearchEndpoint: that means "where boolean mode
  // POSTs", so reusing it would silently reroute traditional search on any form
  // that merely enabled the Boolean toggle.
  const unifiedEndpoint = String(schema?.searchEndpoint ?? '').trim();

  // One vocabulary on the wire for both modes: booleanApiKey-mapped params,
  // plus fieldFilters for anything the admin added that has no mapping — which
  // is what keeps custom search fields filtering once the flat query-param
  // path (buildTraditionalParams) is no longer used.
  const buildCriteria = useCallback((values) => {
    const conditions = buildFilterConditions(values, fields);
    return {
      ...buildBooleanRequest(values, fields, {
        defaultSourceType: schema?.booleanDefaultSourceType || 'internal',
      }),
      ...(conditions.length ? { fieldFilters: conditions } : {}),
    };
  }, [fields, schema]);

  const searchFetcher = useMemo(() => {
    if (!unifiedEndpoint || !criteria) return null;
    return makeSearchFetcher({
      endpoint: unifiedEndpoint,
      criteria,
      searchMode: isBooleanMode ? 'boolean' : 'traditional',
      sources: getSearchSources(formValues?.selectedSource),
      sourceModules,
    });
  }, [unifiedEndpoint, criteria, isBooleanMode, formValues, sourceModules]);

  // An action that declares BOTH apiPath and routeTemplate means "call the API,
  // then go somewhere" — e.g. Migrate To Internal, which migrates the candidate
  // and then opens its detail view so the user can tag it to a job or move it
  // to a stage. Neither built-in branch does both: actionType "api" calls and
  // stays put, "navigate" goes without calling. Attaching onClick short-circuits
  // ListView's own dispatch (it returns as soon as onClick exists), so this needs
  // no change to the shared component.
  const withPostActionRoute = useCallback((action) => {
    if (!action?.apiPath || !action?.routeTemplate) return action;

    return {
      ...action,
      onClick: async ({ selectedRecords }) => {
        const record = selectedRecords?.[0] ?? {};
        const apiPath = safeNavTarget(action.apiPath, record._id ?? record.id, record);
        if (!apiPath) {
          message.error(`${action.label}: the configured API path is not a valid in-app path.`);
          return;
        }

        try {
          const method = String(action.apiMethod || 'POST').toUpperCase();
          const response = await fetchJsonWithAuth(AUTH_URL, apiPath, {
            method,
            body: ['GET', 'DELETE'].includes(method)
              ? undefined
              : JSON.stringify({ ids: [record._id ?? record.id], records: [record] }),
          });
          message.success(`${action.label} completed`);

          // The destination may need an id the API only just created, so the
          // response wins over the row it was launched from.
          const context = { ...record, ...(response?.data ?? response ?? {}) };
          const target = safeNavTarget(action.routeTemplate, context._id ?? context.id, context);
          if (target) navigate(target);
        } catch (error) {
          message.error(error?.message || `${action.label} failed`);
        }
      },
    };
  }, [navigate]);

  // ListView reads actions straight out of whatever the mapper returns, so this
  // is where per-action behaviour gets attached without touching ListView.
  const searchResponseMapper = useCallback((response, mapperModule) => {
    const mapped = mapListResponse(response, mapperModule);
    return { ...mapped, actions: (mapped.actions ?? []).map(withPostActionRoute) };
  }, [withPostActionRoute]);

  const handleSearch = () => {
    // Each boolean-capable field carries its own blocking + warningOnly rule
    // (see the Form.Item below), so a malformed expression is rejected right
    // here by antd's own validation — no separate re-check needed.
    form.validateFields()
      .then((values) => {
        if (unifiedEndpoint) {
          // No fetch here — ListView does it. A fresh nonce remounts it, which
          // resets page and tab together for the new criteria.
          setCriteria(buildCriteria(values));
          setSearchNonce((nonce) => nonce + 1);
          return;
        }
        runSearch(values, undefined, { current: 1 });
      })
      .catch(() => message.error('Fix the highlighted field before searching.'));
  };

  const handleReset = () => {
    form.resetFields();
    setFormValues({});
    setFieldErrors({});
    setResults(EMPTY_RESULTS);
    setTabs([]);
    setSelectedSource('internal');
    setCriteria(null);
  };

  const handleSourceAction = useCallback(async (sourceAction, record) => {
    const key = getSourceActionInstanceKey(sourceAction, record);
    setSourceActionState((current) => ({ ...current, [key]: { ...current[key], loading: true } }));
    try {
      await runSourceAction(sourceAction, record);
      setSourceActionState((current) => ({ ...current, [key]: { loading: false, completed: true } }));
      message.success(sourceAction.successMessage);
    } catch (error) {
      setSourceActionState((current) => ({ ...current, [key]: { loading: false } }));
      message.error(error?.message || sourceAction.errorMessage);
    }
  }, []);

  const resultFields = useMemo(() => {
    const base = results.fields?.length ? results.fields : (() => {
      const first = results.rows[0];
      if (!first) return [];
      return Object.keys(first)
        .filter((key) => !key.startsWith('_') && typeof first[key] !== 'object')
        .map((key) => ({
          value: key,
          field: key,
          label: key.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase()),
          isVisible: true,
        }));
    })();

    const sourceActions = getAvailableSourceActions({ rows: results.rows, selectedSource });
    if (!sourceActions.length) return base;

    const actionFields = new Set(sourceActions.map((item) => item.field));
    return [
      ...base.filter((field) => field.cardRole !== 'action' && !actionFields.has(field.value)),
      ...sourceActions.map((sourceAction) => ({
        value: sourceAction.field,
        field: sourceAction.field,
        label: sourceAction.label ?? 'Action',
        cardRole: 'action',
        isVisible: true,
        sorter: false,
        render: (_value, record) => {
          if (!sourceActionAppliesToRecord(sourceAction, record, selectedSource)) return null;
          const key = getSourceActionInstanceKey(sourceAction, record);
          const state = sourceActionState[key] ?? {};
          return (
            <Button
              type="primary"
              size="small"
              loading={state.loading}
              disabled={!key || state.completed}
              onClick={() => handleSourceAction(sourceAction, record)}
            >
              {state.completed ? sourceAction.completedLabel : sourceAction.buttonLabel}
            </Button>
          );
        },
      })),
    ];
  }, [handleSourceAction, results.fields, results.rows, selectedSource, sourceActionState]);

  // The record context this search runs in (e.g. the job being sourced for).
  // Shared by row actions and the configured link button's URL template, so
  // the two can never disagree about which record they mean.
  const actionContext = useMemo(
    () => searchActionContext ?? (migrationJobId ? { jobId: migrationJobId } : null),
    [searchActionContext, migrationJobId],
  );

  const resultActions = useMemo(() => {
    if (!actionContext) return results.actions;
    const merge = (item) => ({
      ...item,
      popupState: { ...actionContext, ...item.popupState },
      ...(item.children ? { children: item.children.map(merge) } : {}),
    });
    return (results.actions ?? []).map(merge);
  }, [results.actions, actionContext]);

  // Admin-configured link button (schema.actionButton*). Off by default, so a
  // form that never enables it renders exactly as it does today.
  // safeNavTarget resolves :tokens AND rejects anything that isn't a plain
  // in-app path — an admin-supplied URL is never interpolated raw.
  const actionButtonEnabled = Boolean(schema?.actionButtonEnabled);
  const actionButtonTarget = actionButtonEnabled
    ? safeNavTarget(schema?.actionButtonUrl, undefined, actionContext ?? {})
    : null;

  const handleActionButton = () => {
    if (!actionButtonTarget) return;
    if (schema?.actionButtonTarget === '_blank') {
      window.open(actionButtonTarget, '_blank', 'noopener');
      return;
    }
    navigate(actionButtonTarget);
  };

  // Boolean mode mirrors the backend's own EMPTY_QUERY rule: at least one
  // boolean-capable field must carry a value — filling only Location or
  // Experience is not a boolean search. Traditional mode keeps the existing
  // "any criterion at all" rule.
  const hasBooleanCriteria = booleanFields.some((field) => {
    const value = formValues[field.field];
    return value !== undefined && value !== null && String(value).trim() !== '';
  });
  const searchDisabled = isBooleanMode ? !hasBooleanCriteria : !hasCriteria(formValues);
  const emptyBooleanHint = isBooleanMode && !hasBooleanCriteria && booleanFields.length
    ? `Enter a query in at least one of: ${booleanFields.map((field) => field.label ?? field.field).join(', ')}.`
    : '';

  if (!moduleName) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card"><Text type="danger">moduleName is required.</Text></Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card"><Skeleton active paragraph={{ rows: 4 }} /></Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card">
          <Space direction="vertical" align="center" style={{ width: '100%' }}>
            <Text strong>Failed to load the search form</Text>
            <Text type="secondary">{loadError}</Text>
            <Button icon={<ReloadOutlined />} onClick={retry}>Retry</Button>
          </Space>
        </Card>
      </div>
    );
  }

  if (!fields.length) {
    return (
      <div className="sbv1-page">
        <Card className="sbv1-card">
          <Text type="secondary">No search fields are configured for “{moduleName}”.</Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="sbv1-page">
      <Card
        className="sbv1-card"
        title={<Text strong>{title ?? schema?.label ?? 'Search'}</Text>}
        extra={(
          <Space size={12}>
            {/* Every action lives up here in one row — nothing at the bottom of
                the field grid, so the controls never move as the form grows. */}
            <Button
              size="small"
              className="sbv1-action-btn"
              icon={<FilterOutlined />}
              onClick={handleReset}
            >
              Reset filters
            </Button>
            <Tooltip title={emptyBooleanHint || undefined}>
              <Button
                size="small"
                className="sbv1-action-btn"
                icon={<SearchOutlined />}
                disabled={searchDisabled}
                loading={results.loading}
                onClick={handleSearch}
              >
                Search
              </Button>
            </Tooltip>
            {actionButtonEnabled && (
              // Disabled rather than hidden when the URL is missing or unsafe,
              // so a half-finished config is visible instead of silently dead.
              <Tooltip title={actionButtonTarget ? undefined : 'No valid destination configured'}>
                <Button
                  size="small"
                  className="sbv1-action-btn"
                  icon={<FileTextOutlined />}
                  disabled={!actionButtonTarget}
                  onClick={handleActionButton}
                >
                  {schema?.actionButtonLabel || 'Open'}
                </Button>
              </Tooltip>
            )}
            {onBack && (
              <Button size="small" className="sbv1-action-btn" onClick={onBack}>Back</Button>
            )}
            {booleanEnabled && (
              <Tooltip title="Switch the marked fields to AND / OR / NOT expressions">
                <Space size={8}>
                  <Text type="secondary">Boolean</Text>
                  <Switch
                    size="small"
                    checked={searchMode === 'boolean'}
                    onChange={handleModeChange}
                  />
                </Space>
              </Tooltip>
            )}
          </Space>
        )}
      >
        <Form
          form={form}
          layout="vertical"
          colon={false}
          onValuesChange={(_changed, all) => setFormValues(all)}
        >
          <Row gutter={[24, 0]}>
            {fields.map((field) => {
              const asBoolean = isBooleanMode && booleanFields.includes(field);
              const rules = [];
              if (truthy(field.req)) {
                rules.push({ required: true, message: `${field.label ?? field.field} is required` });
              }
              if (asBoolean) {
                // Blocking: a broken expression (unbalanced brackets, a
                // dangling operator, "&&" instead of "AND") stops the search —
                // the Go parser would reject it too, so this just saves the
                // round trip.
                rules.push({
                  validator: (_rule, value) => {
                    const { errors } = validateBooleanQuery(value);
                    return errors.length ? Promise.reject(new Error(errors[0])) : Promise.resolve();
                  },
                });
                // warningOnly: legal but likely-wrong (all-exclusions, a bare
                // "*"/"?" the backend treats as a literal character, …) shows
                // as an amber hint and never blocks form.validateFields().
                rules.push({
                  warningOnly: true,
                  validator: (_rule, value) => {
                    const { warnings } = validateBooleanQuery(value);
                    return warnings.length ? Promise.reject(new Error(warnings[0])) : Promise.resolve();
                  },
                });
              }
              return (
                <Col xs={24} sm={12} lg={field.col ?? 12} key={field.field}>
                  <Form.Item
                    name={field.field}
                    label={field.label ?? field.field}
                    rules={rules.length ? rules : undefined}
                    initialValue={lockedInitialValue(field)}
                  >
                    {asBoolean
                      ? <BooleanQueryField field={field} serverError={fieldErrors[field.field]} />
                      : <SearchFieldControl field={field} moduleName={moduleName} />}
                  </Form.Item>
                </Col>
              );
            })}
          </Row>

          {/* The buttons moved to the card header; the hint stays down here
              next to the fields it is talking about. */}
          {emptyBooleanHint && (
            <div className="sbv1-actions">
              <Text type="secondary" className="sbv1-empty-hint">{emptyBooleanHint}</Text>
            </div>
          )}
        </Form>
      </Card>

      {/* Unified path: ListView owns fetching, tabs, paging, sort and
          selection. Deliberately no onPageChange/onPageSizeChange/onSearch —
          those set isControlled and switch its own fetching off. */}
      {unifiedEndpoint && searchFetcher && (
        <ListView
          key={searchNonce}
          moduleName={moduleName}
          breadcrumbItems={[]}
          fetchData={searchFetcher}
          responseMapper={searchResponseMapper}
          queryParams={criteria}
          rowKey={resolveRowKey}
          showSearch={false}
          activeView={activeView}
          onViewChange={setActiveView}
          showViewToggle
        />
      )}

      {!unifiedEndpoint && results.searched && (
        <ListView
          moduleName={moduleName}
          breadcrumbItems={[]}
          dataSource={results.rows}
          fields={resultFields}
          total={results.total}
          loading={results.loading}
          current={pagination.current}
          pageSize={pagination.pageSize}
          showSearch={false}
          tabs={tabs}
          selectedSource={selectedSource}
          onTabChange={(key) => runSearch(lastValues ?? {}, key, { current: 1 })}
          onPageChange={(current, pageSize) => runSearch(lastValues ?? {}, selectedSource, { current, pageSize })}
          onPageSizeChange={(current, pageSize) => runSearch(lastValues ?? {}, selectedSource, { current: 1, pageSize })}
          actions={resultActions}
          actionRules={results.actionRules}
        />
      )}
    </div>
  );
}

SearchBuilderV1.propTypes = {
  moduleName: PropTypes.string.isRequired,
  action: PropTypes.string,
  formType: PropTypes.string,
  title: PropTypes.string,
  migrationJobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchActionContext: PropTypes.object,
  // { [sourceKey]: moduleKey } — see resolveSourceModule.
  sourceModules: PropTypes.object,
  onBack: PropTypes.func,
};
